import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import SearchIcon from "@mui/icons-material/Search";
import Title from "../../../components/Title";
import HeaderResponsive from "../../../components/layouts/HeaderResponsive";
import { stickyListRegionWidth } from "../../../components/layouts/StickyListToolbar";
import { useDrawer } from "../../../components/context/DrawerContext";
import { useSelection } from "../../../components/context/SelectionContext";
import CollectByOfficeForm from "../../../components/filter/collectbyOffice";
import { Office } from "../../../models/office";
import { Collect } from "../../../models/collect";
import SimpleCustomTable from "../../../components/table/SimpleCustomTable";
import { SimpleColumn, SimpleRow, RowId } from "../../../components/table/interface";
import EmptyStateMessage from "../../../components/layouts/EmptyStateMessage";
import { nowOperationalDateString } from "../../../utils/operationalDateTime";
import PermissionButton from "../../../components/common/PermissionButton";
import { useResourcePermissions } from "../../../hooks/useResourcePermissions";

/** Mostrar flags de pendiente / no visita en tabla y resumen. */
const SHOW_PENDING_NOVISIT_IN_SEGUIMIENTO = true;

interface ShowAlerts {
  showAlert: (message: string, severity?: "success" | "info" | "warning" | "error") => void;
  showLoading: () => void;
  hideLoading: () => void;
}

interface RouteDailyMeta {
  id: number;
  date: string;
  state: number;
  state_label: string;
  count_lend: number;
  collect_id: number;
  collect_name: string;
  user_id: number | null;
  username: string | null;
}

interface RouteDailyRow {
  route_id: number;
  lend_id: number;
  position: number;
  idnumber: string;
  fullname: string;
  balance: number;
  lend_state?: number;
  constant_name?: string;
  type_constant_name?: string;
  sync: boolean;
  pending: boolean;
  novisit: boolean;
  payed: boolean;
}

interface Summary {
  total: number;
  synced: number;
  notSynced: number;
  pending: number;
  novisit: number;
  payed: number;
}

/** Texto de plazo según type_constant (BD suele guardar Diaria, Semanal, …). */
function plazoTipoLabel(raw: string | undefined | null): string {
  const t = String(raw ?? "").trim();
  if (!t) return "—";
  const lower = t.toLowerCase();
  if (lower === "diaria" || lower === "diario") return "Diario";
  if (lower === "semanal") return "Semanal";
  if (lower === "quincenal") return "Quincenal";
  if (lower === "mensual") return "Mensual";
  return t;
}

const boolChip = (field: string, header: string, okColor: "success" | "warning" | "error" | "info" | "default"): SimpleColumn => ({
  field,
  headerName: header,
  width: "88px",
  align: "center",
  renderCell: ({ row }) => {
    const v = Boolean((row as Record<string, unknown>)[field]);
    return (
      <Chip
        size="small"
        label={v ? "Sí" : "No"}
        color={v ? okColor : "default"}
        variant={v ? "filled" : "outlined"}
      />
    );
  },
});

const RutaSeguimiento: React.FC<ShowAlerts> = ({ showAlert, showLoading, hideLoading }) => {
  document.title = "Seguimiento ruta del día";
  const { drawerOpen } = useDrawer();
  const { office_id, collect_id, setOffice_id, setCollect_id } = useSelection();
  const { canRead, canUpdate } = useResourcePermissions('/procesos/ruta-seguimiento');
  const [offices, setOffices] = useState<Office[]>([]);
  const [collectsOffice, setCollectsOffice] = useState<Collect[]>([]);
  const [dateStr, setDateStr] = useState(() => nowOperationalDateString());
  const [routeMeta, setRouteMeta] = useState<RouteDailyMeta | null>(null);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [rows, setRows] = useState<RouteDailyRow[]>([]);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [clientFilter, setClientFilter] = useState("");

  useEffect(() => {
    const loadOffices = async () => {
      try {
        const { data } = await axios.get("offices");
        const list = Array.isArray(data?.data) ? data.data : [];
        setOffices(list);
      } catch (e) {
        console.error(e);
      }
    };
    void loadOffices();
  }, []);

  useEffect(() => {
    if (offices.length === 0) return;
    setOffice_id((prev) =>
      prev && !offices.some((o) => String(o.id) === String(prev)) ? "" : prev
    );
  }, [offices, setOffice_id]);

  const clearRouteTrackingTable = useCallback(() => {
    setRouteMeta(null);
    setSummary(null);
    setRows([]);
    setInfoMessage(null);
    setClientFilter("");
    setLoadedOnce(false);
  }, []);

  useEffect(() => {
    clearRouteTrackingTable();
  }, [office_id, clearRouteTrackingTable]);

  useEffect(() => {
    clearRouteTrackingTable();
  }, [collect_id, clearRouteTrackingTable]);

  useEffect(() => {
    if (!office_id) {
      setCollectsOffice([]);
      return;
    }
    let cancelled = false;
    const loadCollects = async () => {
      try {
        const { data } = await axios.get(`collectsoffice/${office_id}`);
        const list = Array.isArray(data) ? data : [];
        if (cancelled) return;
        setCollectsOffice(list);
        setCollect_id((prev) =>
          prev && !list.some((c) => String(c.id) === String(prev)) ? "" : prev
        );
      } catch (e) {
        if (!cancelled) {
          console.error(e);
          setCollectsOffice([]);
        }
      }
    };
    void loadCollects();
    return () => {
      cancelled = true;
    };
  }, [office_id, setCollect_id]);

  const fetchTracking = useCallback(async () => {
    if (!canRead) {
      showAlert("Sin permiso para consultar la ruta.", "warning");
      return;
    }
    if (!collect_id) {
      showAlert("Seleccione un cobro", "warning");
      return;
    }
    showLoading();
    setInfoMessage(null);
    try {
      const { data } = await axios.get<{
        data: { route: RouteDailyMeta; rows: RouteDailyRow[]; summary: Summary } | null;
        message?: string;
      }>("route-daily-tracking", {
        params: { collect_id: Number(collect_id), date: dateStr },
      });
      if (!data?.data) {
        setRouteMeta(null);
        setSummary(null);
        setRows([]);
        setClientFilter("");
        setInfoMessage(data?.message ?? "Sin datos de ruta.");
        showAlert(data?.message ?? "No hay ruta para la fecha indicada.", "info");
        setLoadedOnce(true);
        return;
      }
      setRouteMeta(data.data.route);
      setSummary(data.data.summary);
      setRows(data.data.rows);
      setClientFilter("");
      setInfoMessage(null);
      setLoadedOnce(true);
    } catch (err) {
      console.error(err);
      setRouteMeta(null);
      setSummary(null);
      setRows([]);
      setClientFilter("");
      showAlert("No se pudo cargar el seguimiento de la ruta.", "error");
    } finally {
      hideLoading();
    }
  }, [canRead, collect_id, dateStr, hideLoading, showAlert, showLoading]);

  const todayOperational = nowOperationalDateString();
  const canGenerateRouteToday = Boolean(collect_id) && dateStr === todayOperational;

  const generateRouteDay = useCallback(async () => {
    if (!canUpdate) {
      showAlert("Sin permiso para actualizar la ruta.", "warning");
      return;
    }
    if (!collect_id) {
      showAlert("Seleccione un cobro", "warning");
      return;
    }
    if (dateStr !== nowOperationalDateString()) {
      showAlert("Solo se puede generar o actualizar la ruta para el día actual.", "warning");
      return;
    }
    const ok = window.confirm(
      SHOW_PENDING_NOVISIT_IN_SEGUIMIENTO
        ? "Se limpiará y volverá a armar la ruta abierta para la fecha seleccionada. Se conservan sync, pendiente y no visita por préstamo; «pagó» se cruza con abonos registrados ese día en el cobro (y limpia pendiente/no visita si hay pago). ¿Continuar?"
        : "Se limpiará y volverá a armar la ruta abierta para la fecha seleccionada. Se conserva el estado de sync por préstamo; «pagó» se cruza con abonos registrados ese día en el cobro. ¿Continuar?"
    );
    if (!ok) return;
    showLoading();
    try {
      const { data } = await axios.post<{ message?: string }>("route-generate-day", {
        collect_id: Number(collect_id),
        date: dateStr,
      });
      showAlert(data?.message ?? "Ruta actualizada.", "success");
      await fetchTracking();
    } catch (err) {
      console.error(err);
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        "No se pudo generar la ruta.";
      showAlert(msg, "error");
    } finally {
      hideLoading();
    }
  }, [canUpdate, collect_id, dateStr, fetchTracking, hideLoading, showAlert, showLoading]);

  const tableRows: SimpleRow[] = useMemo(
    () =>
      rows.map((r) => ({
        ...r,
        id: r.lend_id as RowId,
        balance_fmt: Number(r.balance ?? 0).toLocaleString("es-CO"),
        prestamo_estado: Number(r.lend_state) === 1 ? "Cancelado" : "Vigente",
      })),
    [rows]
  );

  const filteredTableRows: SimpleRow[] = useMemo(() => {
    const q = clientFilter.trim().toLowerCase();
    if (!q) return tableRows;
    return tableRows.filter((r) => {
      const doc = String(r.idnumber ?? "").toLowerCase();
      const name = String(r.fullname ?? "").toLowerCase();
      const tipoRaw = String(r.type_constant_name ?? "").toLowerCase();
      const tipoLabel = plazoTipoLabel(r.type_constant_name).toLowerCase();
      return doc.includes(q) || name.includes(q) || tipoRaw.includes(q) || tipoLabel.includes(q);
    });
  }, [tableRows, clientFilter]);

  const columns: SimpleColumn[] = useMemo(() => {
    const base: SimpleColumn[] = [
      { field: "position", headerName: "Ord.", width: "64px", align: "center" },
      { field: "idnumber", headerName: "Cédula", minWidth: 120 },
      { field: "fullname", headerName: "Cliente", minWidth: 180, wrapText: true },
      { field: "balance_fmt", headerName: "Saldo", width: "100px", align: "right" },
      { field: "prestamo_estado", headerName: "Préstamo", width: "100px" },
      {
        field: "type_constant_name",
        headerName: "Tipo",
        minWidth: 112,
        renderCell: ({ row }) =>
          plazoTipoLabel(String((row as Record<string, unknown>).type_constant_name ?? "")),
      },
      boolChip("sync", "Sync", "success"),
    ];
    if (SHOW_PENDING_NOVISIT_IN_SEGUIMIENTO) {
      base.push(
        boolChip("pending", "Pendiente", "warning"),
        boolChip("novisit", "No visita", "error")
      );
    }
    base.push(boolChip("payed", "Pagó", "info"));
    return base;
  }, []);

  return (
    <HeaderResponsive>
      <Title>Seguimiento ruta del día</Title>
      <CardContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: "100%" }}>
          Consulta la ruta para la fecha elegida y el estado que reporta la app. Si no existe o quedó
          incompleta y la fecha de consulta es hoy, usa «Actualizar ruta» (solo día actual): repuebla el
          listado, conserva sync y alinea «pagó» con abonos del día en el cobro.
          {SHOW_PENDING_NOVISIT_IN_SEGUIMIENTO
            ? " También se reflejan pendiente y no visita."
            : ""}
        </Typography>
      </CardContent>
      <Card
        sx={{
          width: (t) => stickyListRegionWidth(t, drawerOpen),
          maxWidth: "100%",
          boxSizing: "border-box",
          overflowX: "auto",
        }}
      >
        <Box sx={{ pb: 0.5 }}>
          <CollectByOfficeForm offices={offices} collectsOffice={collectsOffice} />
        </Box>
        <CardContent sx={{ pt: 0.5 }}>
          <Grid container spacing={5} alignItems="center" sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <TextField
                size="small"
                fullWidth
                type="date"
                label="Fecha de la ruta"
                InputLabelProps={{ shrink: true }}
                value={dateStr}
                onChange={(e) => setDateStr(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                sx={{ width: 1, "& .MuiButton-root": { flex: 1, minWidth: 0 } }}
              >
                <PermissionButton
                  allowed={canRead}
                  deniedTooltip="Sin permiso para consultar"
                  tooltip="Consultar"
                  fullWidth
                  variant="contained"
                  startIcon={<RefreshIcon />}
                  onClick={() => void fetchTracking()}
                >
                  Consultar
                </PermissionButton>
                <PermissionButton
                  allowed={canUpdate}
                  deniedTooltip="Sin permiso para actualizar la ruta"
                  tooltip="Actualizar ruta"
                  fullWidth
                  variant="outlined"
                  color="secondary"
                  startIcon={<AutoFixHighIcon />}
                  disabled={!canGenerateRouteToday}
                  title={
                    canGenerateRouteToday
                      ? undefined
                      : "Disponible solo cuando la fecha de la ruta es el día actual."
                  }
                  onClick={() => void generateRouteDay()}
                >
                  Actualizar ruta
                </PermissionButton>
              </Stack>
            </Grid>
          </Grid>

          {routeMeta && (
            <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
              <Chip label={`Ruta #${routeMeta.id}`} size="small" />
              <Chip label={routeMeta.state_label} color="primary" variant="outlined" size="small" />
              <Chip
                label={`Registrados: ${routeMeta.count_lend ?? summary?.total ?? 0}`}
                size="small"
                variant="outlined"
              />
              {routeMeta.username && (
                <Chip label={`Usuario: ${routeMeta.username}`} size="small" variant="outlined" />
              )}
            </Stack>
          )}

          {summary && (
            <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
              <Chip label={`Total filas: ${summary.total}`} size="small" />
              <Chip label={`Sync Sí: ${summary.synced}`} color="success" size="small" variant="outlined" />
              <Chip label={`Sin sync: ${summary.notSynced}`} size="small" variant="outlined" />
              {SHOW_PENDING_NOVISIT_IN_SEGUIMIENTO && (
                <>
                  <Chip label={`Pendiente: ${summary.pending}`} color="warning" size="small" variant="outlined" />
                  <Chip label={`No visita: ${summary.novisit}`} color="error" size="small" variant="outlined" />
                </>
              )}
              <Chip label={`Pagó: ${summary.payed}`} color="info" size="small" variant="outlined" />
            </Stack>
          )}

          {infoMessage && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {infoMessage}
            </Alert>
          )}

          {rows.length > 0 ? (
            <>
              <Grid container spacing={2} alignItems="center" sx={{ mb: 1.5 }}>
                <Grid item xs={12} md={6} lg={5}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Filtrar por cédula o nombre"
                    value={clientFilter}
                    onChange={(e) => setClientFilter(e.target.value)}
                    placeholder="Escriba para acotar los datos de la tabla"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    {clientFilter.trim()
                      ? `Mostrando ${filteredTableRows.length} de ${tableRows.length} registros`
                      : `${tableRows.length} registros en la ruta`}
                  </Typography>
                </Grid>
              </Grid>
              {filteredTableRows.length > 0 ? (
                <SimpleCustomTable
                  rows={filteredTableRows}
                  columns={columns}
                  maxHeight={520}
                  showBorders
                  showCount
                  exportCsv
                  exportFileName={`ruta-seguimiento-${dateStr}.csv`}
                  wrapCellContent
                />
              ) : (
                <EmptyStateMessage
                  message="Ningún registro coincide con cédula o nombre buscados."
                  align="center"
                />
              )}
            </>
          ) : loadedOnce && !infoMessage ? (
            <EmptyStateMessage message="Sin préstamos en la ruta para esta fecha." align="center" />
          ) : !loadedOnce ? (
            <EmptyStateMessage
              message="Seleccione cobro y fecha, luego pulse Consultar."
              align="center"
            />
          ) : null}
        </CardContent>
      </Card>
    </HeaderResponsive>
  );
};

export default RutaSeguimiento;
